import { useState, useEffect } from "react";
import axios from "axios";
import { hapticTap, hapticSuccess, hapticError } from "../utils/haptics";
import { soundWin } from "../utils/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fN = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d);

function parseOCC(sym) {
  if (!sym) return null;
  const m = sym.match(/^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
  if (!m) return null;
  const [, ticker, yy, mm, dd, cp, rawStrike] = m;
  return { ticker, strike: parseInt(rawStrike) / 1000, side: cp === "C" ? "Call" : "Put", expDate: `${mm}/${dd}/${yy}` };
}

/**
 * TrailStopModal — arm a server-side trailing stop on an existing position.
 * Server monitors peak price every 15s and auto-sells when it drops `trailPct` from peak.
 */
export default function TrailStopModal({ position, onClose, onSuccess }) {
  const isOption = String(position.type || "").includes("OPTION") || !!parseOCC(position.symbol);
  const isCrypto = String(position.type || "").includes("CRYPTO");
  const occ = isOption ? parseOCC(position.symbol) : null;
  const displayTicker = occ ? occ.ticker : position.ticker || position.symbol;
  const heldQty = Math.abs(Number(position.quantity || 0)) || 0;

  const [trailPct, setTrailPct] = useState(0.25);
  const [livePrice, setLivePrice] = useState(position.lastPrice || 0);
  const [existing, setExisting] = useState(null); // already-armed stop for this symbol, if any
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState("");

  const PRESETS = [
    { pct: 0.10, label: "Tight · 10%", desc: "Lock gains aggressively" },
    { pct: 0.15, label: "15%", desc: "Balanced protection" },
    { pct: 0.25, label: "Classic · 25%", desc: "Standard swing trail" },
    { pct: 0.40, label: "40%", desc: "Loose — let it ride" },
  ];

  // Live mid price
  useEffect(() => {
    const sym = position.symbol;
    if (!sym) return;
    const load = () => {
      if (isOption) {
        axios.get(`${API}/market/greeks?osi=${sym}`).then(r => {
          const d = r.data || {};
          if (d.mid != null) setLivePrice(d.mid);
          else if (d.bid != null && d.ask != null) setLivePrice((d.bid + d.ask) / 2);
        }).catch(() => {});
      } else {
        axios.get(`${API}/market/quotes?symbols=${displayTicker}`).then(r => {
          const q = Array.isArray(r.data) ? r.data[0] : r.data;
          if (q?.price) setLivePrice(q.price);
        }).catch(() => {});
      }
    };
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [position.symbol, isOption, displayTicker]);

  // Check for existing armed stop on this symbol
  useEffect(() => {
    axios.get(`${API}/trail-stops?status=armed`).then(r => {
      const match = (r.data?.stops || []).find(s => s.symbol === position.symbol);
      setExisting(match || null);
    }).catch(() => {});
  }, [position.symbol]);

  const triggerPrice = livePrice * (1 - trailPct);
  const dollarsProtected = (livePrice - triggerPrice) * heldQty * (isOption ? 100 : 1);

  const arm = async () => {
    hapticTap();
    setPlacing(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/trail-stop/arm`, {
        symbol: position.symbol,
        ticker: displayTicker,
        quantity: heldQty,
        trailPct,
        instrumentType: isOption ? "OPTION" : (isCrypto ? "CRYPTO" : "EQUITY"),
      });
      if (res.data?.ok === false) {
        hapticError();
        setErr(res.data?.error || "Failed to arm trail stop");
      } else {
        hapticSuccess();
        soundWin();
        onSuccess?.(res.data?.stop);
        onClose();
      }
    } catch (e) {
      hapticError();
      setErr(e.response?.data?.detail || e.message || "Failed to arm trail stop");
    }
    setPlacing(false);
  };

  const cancelExisting = async () => {
    if (!existing) return;
    hapticTap();
    try {
      await axios.post(`${API}/trail-stop/${existing.stop_id}/cancel`);
      setExisting(null);
      hapticSuccess();
    } catch (e) {
      hapticError();
      setErr(e.response?.data?.detail || "Cancel failed");
    }
  };

  const C = "#fbbf24";

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 10099, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      data-testid="trail-stop-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(440px, 100%)", background: "#0d1117", border: `1px solid ${C}40`, borderRadius: 18, padding: 22, boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px ${C}22` }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: C, letterSpacing: 1 }}>ARM TRAILING STOP</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>Trail Stop</div>
          </div>
          <button
            onClick={onClose}
            data-testid="ts-close"
            aria-label="Close"
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
              color: "#e6edf3", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Contract */}
        <div style={{ padding: 12, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#e6edf3" }}>{displayTicker}</div>
          {occ && <div style={{ fontSize: 12, color: "#60a5fa", marginTop: 2 }}>${occ.strike} {occ.side} · Exp {occ.expDate}</div>}
          <div style={{ fontSize: 11, color: "#8b949e", marginTop: 6 }}>
            Live: <strong style={{ color: "#e6edf3" }}>${fN(livePrice, isOption ? 2 : 4)}</strong> · {heldQty}{isOption ? " contract" + (heldQty !== 1 ? "s" : "") : " sh"}
          </div>
        </div>

        {/* Existing stop warning */}
        {existing && (
          <div style={{ padding: 12, borderRadius: 10, background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.24)", marginBottom: 12 }} data-testid="ts-existing">
            <div style={{ fontSize: 10, color: "#60a5fa", fontWeight: 700, marginBottom: 4 }}>ALREADY ARMED · {(existing.trailPct * 100).toFixed(0)}% trail</div>
            <div style={{ fontSize: 11, color: "#c9d1d9", marginBottom: 8 }}>
              Peak ${fN(existing.peakPrice, 2)} · Trigger ${fN(existing.peakPrice * (1 - existing.trailPct), 2)}
            </div>
            <button onClick={cancelExisting} data-testid="ts-cancel-existing" style={{ width: "100%", padding: 8, borderRadius: 8, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.28)", color: "#f87171", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Cancel Existing Stop</button>
          </div>
        )}

        {/* Preset pills */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
          {PRESETS.map(p => {
            const active = Math.abs(trailPct - p.pct) < 0.001;
            return (
              <button
                key={p.pct}
                data-testid={`ts-preset-${Math.round(p.pct * 100)}`}
                onClick={() => { hapticTap(); setTrailPct(p.pct); }}
                style={{
                  padding: "10px 12px", borderRadius: 10, textAlign: "left",
                  background: active ? `${C}18` : "rgba(255,255,255,0.02)",
                  border: `1px solid ${active ? C + "60" : "rgba(255,255,255,0.06)"}`,
                  color: active ? C : "#c9d1d9",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700 }}>{p.label}</div>
                <div style={{ fontSize: 9, color: active ? C : "#636e7b", marginTop: 2 }}>{p.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Custom slider */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#636e7b", marginBottom: 6 }}>
            <span>CUSTOM TRAIL</span>
            <strong style={{ color: C, fontSize: 14 }}>{(trailPct * 100).toFixed(0)}%</strong>
          </div>
          <input
            data-testid="ts-slider"
            type="range" min="0.05" max="0.60" step="0.01"
            value={trailPct}
            onChange={e => setTrailPct(Number(e.target.value))}
            style={{ width: "100%", accentColor: C }}
          />
        </div>

        {/* Projection */}
        <div style={{ padding: 12, borderRadius: 10, background: `${C}08`, border: `1px solid ${C}20`, marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C, fontWeight: 700, marginBottom: 6 }}>IF PRICE DROPS {(trailPct * 100).toFixed(0)}% FROM PEAK</div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8b949e", marginBottom: 4 }}>
            <span>Initial peak</span><strong style={{ color: "#e6edf3" }}>${fN(livePrice, 2)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8b949e", marginBottom: 4 }}>
            <span>Trigger price</span><strong style={{ color: "#f87171" }}>${fN(triggerPrice, 2)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8b949e" }}>
            <span>Worst-case loss from peak</span><strong style={{ color: "#fbbf24" }}>-${fN(dollarsProtected, 2)}</strong>
          </div>
        </div>

        {err && <div style={{ padding: 10, borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.24)", marginBottom: 12, fontSize: 11, color: "#f87171" }} data-testid="ts-error">{err}</div>}

        <button
          onClick={arm}
          disabled={placing || heldQty <= 0}
          data-testid="ts-arm-btn"
          style={{
            width: "100%", padding: 14, borderRadius: 30,
            background: placing ? "#636e7b" : C,
            color: "#0d1117", fontSize: 15, fontWeight: 800, border: "none",
            cursor: placing ? "default" : "pointer",
            boxShadow: placing ? "none" : `0 6px 20px ${C}55`,
          }}
        >{placing ? "Arming…" : `Arm ${(trailPct * 100).toFixed(0)}% Trail · ${heldQty}${isOption ? " contract" + (heldQty !== 1 ? "s" : "") : ""}`}</button>

        <div style={{ textAlign: "center", fontSize: 9, color: "#636e7b", marginTop: 10, lineHeight: 1.5 }}>
          Server monitors price every 15s. Triggers a SELL limit order at current price when the trail is hit.
        </div>
      </div>
    </div>
  );
}
